import { Status } from "@/app/shared_enums/enums";
import { CounterDetails, CounterTable } from "@/app/types/counter";
import CounterForm from "@/components/admin/counter-management/component/modal/CounterForm";
import { apiSlice } from "@/features/api/ApiSlice";
import { service } from '@/mock/services';
import { Code } from "lucide-react";

export const Counter = apiSlice.injectEndpoints({
  endpoints(builder) {
    return {
      addCounter: builder.mutation<
        CounterDetails,
        {
          staff_ids: string[];
          status: Status;
          code: string;
        }
      >({
        query: (counterData) => ({
          url: `/counters_management/`,
          method: "POST",
          body: counterData,
          headers: {
            "Content-Type": "application/json",
          },
        }),
        invalidatesTags: ["Counters"],
      }),
      getAllCounter: builder.query<CounterTable, { query: string }>({
        query: ({ query }) => ({
          url: `/counters_management${query ? "?" + query : ""}`,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }),
        providesTags: ["Counters", "Service"],
      }),
      getCounterList: builder.query<
        {
          value: number;
          label: string;
          service_Name: string;
          AssignedCounter: boolean;
          status: Status;
          code: string;
          staff: { id: string; fullName: string }[];
          is_occupied: boolean;
          occupied_by: string | null;
        }[],
        { query: string }
      >({
        query: ({ query }) => ({
          url: `/counters_management${query ? "?" + query : ""}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Counters", "Service"],
        transformResponse: (resp: {
          data: {
            id: number;
            service_id: number | null;
            status: Status;
            code: string;
            service_Name: string;
            staff_members: {
              id: string;
              first_name: string;
              last_name: string;
            }[];
            is_occupied: boolean;
            occupied_by: string | null;
          }[];
        }) => {
          return resp.data.map((counter) => ({
            value: counter.id,
            label: `${counter.code}`,
            AssignedCounter: typeof counter.service_id === "number",
            status: counter.status, // Adjust the logic here as needed
            service_Name: counter.service_Name,
            code: counter.code,
            staff:
              counter.staff_members?.map((staff) => ({
                id: staff.id,
                fullName: `${staff.first_name} ${staff.last_name}`.trim(),
              })) || [],
            is_occupied: counter.is_occupied,
            occupied_by: counter.occupied_by,
          }));
        },
      }),

      deleteCounter: builder.mutation<CounterDetails, number>({
        query: (counterId) => ({
          url: `/counters_management/${counterId}`,
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        }),
        invalidatesTags: ["Counters"],
      }),

      updateCounter: builder.mutation<
        CounterDetails,
        {
          counterId: number;
          staff_ids: string[];
          status: Status;
          code: string;
        }
      >({
        query({ counterId, staff_ids, status, code }) {
          return {
            url: `/counters_management/${counterId}`,
            method: "PUT",
            body: { staff_ids, status, code },
            headers: {
              "Content-Type": "application/json",
            },
          };
        },
        invalidatesTags: ["Counters"],
      }),
      getCounterById: builder.query<CounterDetails, number>({
        query: (counterId) => ({
          url: `/counters_management/${counterId}`,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }),
        providesTags: ["Counters"],
      }),
      updateCounterOccupiedStatus: builder.mutation<
        CounterDetails,
        {
          staff_id: string;
          id: number;
        }
      >({
        query: (counterId) => ({
          url: `/counters_management/${counterId.id}/occupied?staff_id=${counterId.staff_id}`,
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
        }),
        invalidatesTags: ["Counters", "LiveQueue", "WaitingList"],
      }),
    };
  },
});

export const {
  useAddCounterMutation,
  useGetAllCounterQuery,
  useGetCounterListQuery,
  useDeleteCounterMutation,
  useUpdateCounterMutation,
  useGetCounterByIdQuery,
  useLazyGetCounterByIdQuery,
  useUpdateCounterOccupiedStatusMutation
} = Counter;
